import { useInvoiceReview } from "@/modules/reviews/useInvoiceReview";
import { WalkInPaymentMethod } from "@/models/club";
import { hasUserPayment } from "./hasUserPayment";
import { bankGatewayLabel } from "@/services/payment/bankGateway";
import CardItemLoading from "@/components/loading/carItemLoading";
import {
  AppointmentQueueItemDto,
  AppointmentStatus,
  CreateCustomerPurchaseCommandResponse,
  CreateCustomerPurchaseCommand,
  MerchantAppointmentQueueDto,
  MerchantServiceDto,
} from "@/models/club";
import { Customer } from "@/models/response/customer";
import SelectCustomer from "@/modules/schedule/customer/SelectCustomer";
import useInvoiceStaff, { commissionOf } from "@/modules/peyment/useInvoiceStaff";
import {
  ActionIcon,
  Avatar,
  Button,
  Checkbox,
  Drawer,
  NumberInput,
  Select,
  Switch,
  Textarea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure, useElementSize } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCalendarCheck,
  IconPlus,
  IconSearch,
  IconTrash,
  IconNote,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { UseMutateFunction } from "@tanstack/react-query";
import moment from "moment-jalaali";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useGetCustomerByPhoneNumber from "../../customer/hooks/useGetCustomerByPhoneNumber";
import UseGetCustomerById from "../../customer/hooks/useGetCustomerById";
import { ServiceDto } from "../type";
import { useGetAllAppointmentQueue } from "@/services/payment/utils";
import { DirectMethod, directMethodValue } from "@/services/payment/walkInPaymentMethod";
import InvoiceDiscount, { AppliedDiscount } from "@/modules/peyment/InvoiceDiscount";
import classes from "./addInvoice.module.css";

// A chair renter keeps the whole price: the salon's commission does not apply to them (for now).
const isChairRenter = (member?: unknown) => Boolean((member as { isChairRenter?: boolean } | undefined)?.isChairRenter);


type Props = {
  appointment?: MerchantAppointmentQueueDto;
  mutate: UseMutateFunction<
    CreateCustomerPurchaseCommandResponse,
    unknown,
    CreateCustomerPurchaseCommand,
    unknown
  >;
  isLoading: boolean;
};

// How the amount is received at the counter (SnappPay stays off on this page, as before).
const directMethods: { value: DirectMethod; label: string }[] = [
  { value: "card", label: "کارت بانکی" },
  { value: "cash", label: "نقدی" },
  { value: "transfer", label: "کارت‌به‌کارت" },
  { value: "other", label: "سایر" },
];
const rial = (value: number) => `${Math.round(value).toLocaleString()} ریال`;
const noDiscount: AppliedDiscount = { code: null, amount: 0, pending: false };
// Below this width the service catalog leaves the page for a side drawer, so the items keep room to breathe.
const CATALOG_INLINE_WIDTH = 1180;

// Desktop invoice: catalog | invoice items | summary, laid out like a POS checkout (Fresha, Square, Shopify POS):
// the customer, totals, discount code and the one call to action live together in the summary column.
function AddPaymentDesktopView({ appointment, mutate, isLoading }: Props) {
  const [walkInPaymentMethod, setWalkInPaymentMethod] = useState<WalkInPaymentMethod>(WalkInPaymentMethod.Value1);
  const [previewCustomer, { open: openCustomer, close: closeCustomer }] = useDisclosure(false);
  const [catalogOpened, { open: openCatalog, close: closeCatalog }] = useDisclosure(false);
  const [checked, setChecked] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [filter, setFilter] = useState("");
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [paidServices, setPaidServices] = useState<ServiceDto[]>([]);
  const [discount, setDiscount] = useState<AppliedDiscount>(noDiscount);
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [method, setMethod] = useState<DirectMethod>("card");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { ref: layoutRef, width: layoutWidth } = useElementSize();
  const catalogInline = layoutWidth === 0 || layoutWidth >= CATALOG_INLINE_WIDTH;
  // The three columns fill the window below the header and scroll on their own, so a long invoice scrolls
  // inside its column instead of pushing the page (and the summary) away.
  const [layoutTop, setLayoutTop] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const top = layoutRef.current?.getBoundingClientRect().top;
      if (top !== undefined) setLayoutTop(Math.max(0, Math.round(top)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [layoutRef, appointment?.id]);

  const { teamMembers, allServices, allServicesFetching, commissionLocked } = useInvoiceStaff();
  // Who can be picked for a service: those who offer it, or everyone when nobody has it in their list
  // (otherwise the row could never get a performer and the review SMS stays off).
  const staffFor = (serviceId?: number) => {
    const offering = teamMembers?.filter((t) => t.merchantServices?.some((s) => s.id === serviceId)) ?? [];
    return offering.length ? offering : (teamMembers ?? []);
  };
  const { data: customerById } = UseGetCustomerById({ id: appointment?.customerId || 0 });
  const { data: customerByPhoneNumber } = useGetCustomerByPhoneNumber({
    enabled: !!appointment?.customer?.phoneNumber,
    phoneNumber: appointment?.customer?.phoneNumber || "",
  });
  const appointmentCustomer = customerById || customerByPhoneNumber;

  const { data: appointments, refetch } = useGetAllAppointmentQueue({
    Count: 1000,
    AppointmentDateFrom: moment().format("YYYY-MM-DD"),
    AppointmentDateTo: moment().add(1, "d").format("YYYY-MM-DD"),
    Skip: 0,
    CustomerId: selectedCustomer?.id,
  });

  useEffect(() => {
    if (selectedCustomer?.id && checked) {
      refetch();
    }
  }, [selectedCustomer?.id, checked, refetch]);

  useEffect(() => {
    if (appointments?.result) {
      setServices((prev) => {
        const newItems = appointments?.result?.filter(
          (x) => x.status === AppointmentStatus.Value1 || x.status === AppointmentStatus.Value2,
        );
        const merged = [...prev];
        newItems?.forEach((queue) => {
          queue?.appointments
            ?.filter((x) => !x.purchaseRegistered)
            .forEach((item) => {
              const exist = merged.find((x) => Number(x.id) === item.appointmentId);
              if (!exist) {
                const findService = allServices?.flatMap((x) => x.services).find((x) => x?.id === item.merchantServiceId);
                const find = teamMembers?.find((x) => x.id === item?.merchantUserId);
                merged.push({
                  id: item?.appointmentId?.toString() || "",
                  profession: find || {},
                  service: findService || {},
                  price: findService?.startingPrice || 0,
                  percentage: commissionOf(find),
                  isRemovable: false,
                  isReserved: true,
                  isSelected: false,
                  appointmentDate: item?.appointmentDate,
                  endTime: moment(item?.endTime, "HH:mm:ss").format("HH:mm"),
                  startTime: moment(item?.startTime, "HH:mm:ss").format("HH:mm"),
                  appointmentId: queue?.id,
                  hasPayment: hasUserPayment(queue),
                  paymentPrice: queue?.rateBreakdown?.price,
                });
              }
            });
        });
        return merged;
      });
    }
  }, [appointments]);

  useEffect(() => {
    if (appointmentCustomer) setSelectedCustomer(appointmentCustomer);
  }, [appointmentCustomer]);

  useEffect(() => {
    if (!appointment?.appointments || !allServices || !teamMembers) return;
    setServices((prev) => {
      const newItems = appointment.appointments as AppointmentQueueItemDto[];
      const merged = [...prev];
      newItems
        .filter((x) => !x.purchaseRegistered)
        .forEach((item) => {
          const exist = merged.find((x) => Number(x.id) === item.appointmentId);
          if (!exist) {
            const findService = allServices?.flatMap((x) => x.services).find((x) => x?.id === item.merchantServiceId);
            const find = teamMembers?.find((x) => x.id === item?.merchantUserId);
            merged.push({
              id: item?.appointmentId?.toString() || "",
              profession: find || {},
              service: findService || {},
              price: findService?.startingPrice || 0,
              percentage: commissionOf(find),
              isRemovable: false,
              isReserved: true,
              isSelected: false,
              appointmentDate: item?.appointmentDate,
              endTime: moment(item?.endTime, "HH:mm:ss").format("HH:mm"),
              startTime: moment(item?.startTime, "HH:mm:ss").format("HH:mm"),
              appointmentId: appointment?.id,
              hasPayment: hasUserPayment(appointment),
              paymentPrice: appointment?.rateBreakdown?.price,
            });
          }
        });
      return merged;
    });
  }, [appointment, allServices, teamMembers]);

  const handleRemoveService = (id: string) => {
    setServices((prev) => prev.filter((x) => x.id !== id));
  };

  const handleReservedSelection = (service: ServiceDto, isSelected: boolean) => {
    setServices((prev) =>
      prev.map((item) =>
        item.isReserved && service.appointmentId != null && item.appointmentId === service.appointmentId
          ? { ...item, isSelected }
          : item,
      ),
    );
  };

  const handleAddService = (svc: MerchantServiceDto) => {
    const offering = teamMembers?.filter((t) => t.merchantServices?.some((s) => s.id === svc.id)) ?? [];
    const defaultTeam = offering[0] || (teamMembers?.length === 1 ? teamMembers[0] : null);
    setServices((prev) => [
      ...prev,
      {
        id: uuidv4(),
        service: svc,
        profession: defaultTeam || {},
        price: svc.startingPrice || 0,
        percentage: commissionOf(defaultTeam),
        isRemovable: true,
        isReserved: false,
        isSelected: true,
      },
    ]);
  };

  const handleUpdatePrice = (id: string, price: number) => {
    setServices((prev) => prev.map((x) => (x.id === id ? { ...x, price } : x)));
  };

  const handleUpdatePercentage = (id: string, percentage: number) => {
    setServices((prev) => prev.map((x) => (x.id === id ? { ...x, percentage } : x)));
  };

  const handleUpdateTeam = (id: string, teamId: string | null) => {
    if (!teamId) return;
    const team = teamMembers?.find((t) => t.id?.toString() === teamId);
    if (!team) return;
    setServices((prev) =>
      prev.map((x) => (x.id === id ? { ...x, profession: team, percentage: commissionOf(team) } : x)),
    );
  };

  const summary = useMemo(() => {
    const paidAppointmentQueueIds = new Set<number>();
    return services
      .filter((x) => !x.isReserved || (x.isReserved && x.isSelected))
      .reduce(
        (acc, item) => {
          const price = item.price;
          const commission = (price * item.percentage) / 100;
          const providerShare = price - commission;
          const alreadyCounted = item.appointmentId != null && paidAppointmentQueueIds.has(item.appointmentId);
          const userPayment = item.hasPayment && !alreadyCounted ? item.paymentPrice || 0 : 0;
          if (item.hasPayment && item.appointmentId != null) {
            paidAppointmentQueueIds.add(item.appointmentId);
          }
          acc.totalPrice += price - userPayment;
          acc.totalCommission += commission;
          acc.totalProviderShare += providerShare;
          acc.totalUserPayments += userPayment;
          acc.totalService += price;
          return acc;
        },
        { totalPrice: 0, totalCommission: 0, totalProviderShare: 0, totalUserPayments: 0, totalService: 0 },
      );
  }, [services]);

  // Owed after the deposit; the discount comes off this, and what is left is paid at the counter.
  const owed = Math.max(0, summary.totalPrice);
  const payable = Math.max(0, owed - discount.amount);
  const activeServices = services.filter((x) => !x.isReserved || (x.isReserved && x.isSelected));

  const buildPurchaseItems = () =>
    activeServices.map((x) => ({
      amount: x.price,
      appointmentId: appointment && x.isReserved ? Number(x.id) : undefined,
      appointmentQueueId: appointment && x.isReserved ? x?.appointmentId : undefined,
      isWalkIn: !x?.isReserved,
      walkInPaymentMethod: !x?.isReserved ? walkInPaymentMethod : undefined,
      markupPercentage: x.percentage,
      merchantServiceId: x?.service?.id,
      merchantUserId: x?.profession?.id,
    }));

  const resetFactorForm = () => {
    setSelectedCustomer(null);
    setServices([]);
    setDiscount(noDiscount);
    setNote("");
    setNoteOpen(false);
    setMethod("card");
    setWalkInPaymentMethod(directMethodValue.card);
  };

  const review = useInvoiceReview(
    selectedCustomer,
    activeServices.map((x) => ({ merchantServiceId: x.service?.id, merchantUserId: x.profession?.id })),
    isLoading,
  );

  const createFactor = () => {
    if (isLoading || review.blocked || discount.pending) return;
    if (!selectedCustomer) {
      notifications.show({ message: "لطفا کاربر را انتخاب نمایید", color: "yellow" });
      return;
    }

    mutate(
      {
        customerId: (selectedCustomer as Customer)?.id,
        description: note.trim(),
        reviewInvitation: review.payload,
        items: buildPurchaseItems(),
        discountCode: discount.code && discount.amount > 0 ? discount.code : undefined,
        directAmount: payable,
        creditAmount: 0,
      },
      {
        onSuccess() {
          review.reset();
          notifications.show({ message: "فاکتور با موفقیت ثبت گردید", color: "green" });
          resetFactorForm();
        },
      },
    );
  };

  useEffect(() => {
    const singleItems = appointment?.appointments?.filter((x) => x.purchaseRegistered) ?? [];
    const bulkItems =
      appointments?.result
        ?.flatMap((x) => x.appointments)
        .filter((x): x is AppointmentQueueItemDto => !!x && (x?.purchaseRegistered || false)) ?? [];
    const merged = [...singleItems, ...bulkItems];
    const unique = merged.filter(
      (item, index, self) => index === self.findIndex((x) => x.appointmentId === item.appointmentId),
    ) as ServiceDto[];
    setPaidServices(unique);
  }, [appointment, appointments]);

  const filteredCategories = allServices?.filter((cat) => {
    const matchesCat = !selectedCategory || cat.title === selectedCategory;
    const matchesSearch = cat.services?.some((s) => s.title?.includes(filter));
    return matchesCat && matchesSearch;
  });

  const addFromCatalog = (svc: MerchantServiceDto) => {
    handleAddService(svc);
    // With the catalog in a drawer the invoice is hidden behind it, so say the service was added.
    if (!catalogInline) notifications.show({ message: `«${svc.title}» به فاکتور اضافه شد`, color: "violet", autoClose: 1500 });
  };

  const catalog = (
    <div className={classes.catalog}>
      <div className={classes.catalogHead}>
        <h2>افزودن خدمت</h2>
        <TextInput
          aria-label="جستجوی خدمت"
          value={filter}
          leftSection={<IconSearch size={16} />}
          placeholder="جستجوی خدمت…"
          onChange={(e) => setFilter(e.target.value)}
          rightSection={
            filter ? (
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label="پاک کردن جستجو" onClick={() => setFilter("")}>
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />
        <Select
          aria-label="دسته‌بندی خدمات"
          value={selectedCategory ?? ""}
          onChange={(value) => setSelectedCategory(value || null)}
          data={[
            { value: "", label: "همه‌ی دسته‌ها" },
            ...(allServices ?? [])
              .filter((cat) => cat.title && (cat.services?.length ?? 0) > 0)
              .map((cat) => ({ value: cat.title!, label: `${cat.title} (${(cat.services?.length ?? 0).toLocaleString()})` })),
          ]}
          searchable
          allowDeselect={false}
          nothingFoundMessage="دسته‌ای پیدا نشد"
          comboboxProps={{ withinPortal: true }}
        />
      </div>
      <div className={classes.catalogList}>
        {allServicesFetching ? (
          <CardItemLoading count={5} />
        ) : !filteredCategories?.length ? (
          <p className={classes.emptyText}>خدمتی با این نام پیدا نشد.</p>
        ) : (
          filteredCategories
            .filter((cat) => (cat.services?.length || 0) > 0)
            .map((cat) => (
              <div key={cat.id ?? cat.title} className={classes.catalogGroup}>
                {selectedCategory === null && <span className={classes.catalogGroupTitle}>{cat.title}</span>}
                {cat.services
                  ?.filter((s) => s.title?.includes(filter))
                  .map((svc) => (
                    <UnstyledButton key={svc.id} className={classes.catalogItem} onClick={() => addFromCatalog(svc)}>
                      <span className={classes.catalogItemText}>
                        <strong>{svc.title}</strong>
                        <span>{rial(svc.startingPrice || 0)}</span>
                      </span>
                      <span className={classes.addIcon} aria-hidden="true"><IconPlus size={15} /></span>
                    </UnstyledButton>
                  ))}
              </div>
            ))
        )}
      </div>
    </div>
  );

  const rowGateway = (row: ServiceDto) =>
    bankGatewayLabel(
      (appointment?.id === row.appointmentId ? appointment : appointments?.result?.find((queue) => queue.id === row.appointmentId))?.bankGateway,
    );

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <div>
          <h1>ثبت فاکتور</h1>
          <p>خدمات انجام‌شده را اضافه کنید، تخفیف را اعمال کنید و مبلغ را دریافت کنید.</p>
        </div>
        <Switch
          checked={checked}
          onChange={(event) => setChecked(event.currentTarget.checked)}
          color="violet"
          label="نوبت‌های امروز این مشتری"
          disabled={!selectedCustomer}
        />
      </header>

      <div ref={layoutRef} className={classes.layout} data-catalog={catalogInline ? "inline" : "drawer"} style={{ height: layoutTop ? `max(480px, calc(100dvh - ${layoutTop + 76}px))` : undefined }}>
        {catalogInline && <aside className={classes.panel} aria-label="خدمات سالن">{catalog}</aside>}

        <section className={`${classes.panel} ${classes.itemsPanel}`} aria-labelledby="invoice-items-title">
          <div className={classes.sectionHead}>
            <h2 id="invoice-items-title">خدمات فاکتور</h2>
            {activeServices.length > 0 && <span className={classes.countPill}>{activeServices.length.toLocaleString()} خدمت</span>}
            {!catalogInline && (
              <Button size="sm" color="violet" variant="light" leftSection={<IconPlus size={16} />} onClick={openCatalog} ms="auto">افزودن خدمت</Button>
            )}
          </div>

          <div className={classes.itemsScroll}>
            {services.length === 0 ? (
              <div className={classes.empty}>
                <IconCalendarCheck size={28} aria-hidden="true" />
                <strong>هنوز خدمتی اضافه نشده</strong>
                <span>{catalogInline ? "از فهرست کنار صفحه خدمت انجام‌شده را اضافه کنید." : "خدمت انجام‌شده را از «افزودن خدمت» اضافه کنید."}</span>
                {!catalogInline && <Button color="violet" leftSection={<IconPlus size={16} />} onClick={openCatalog}>افزودن خدمت</Button>}
              </div>
            ) : (
              <div className={classes.items} role="table" aria-label="خدمات فاکتور">
                <div className={classes.itemsHeadRow} role="row">
                  <span role="columnheader" aria-label="انتخاب" />
                  <span role="columnheader">خدمت</span>
                  <span role="columnheader">انجام‌دهنده</span>
                  <span role="columnheader">مبلغ (ریال)</span>
                  <span role="columnheader">کمیسیون</span>
                  <span role="columnheader" aria-label="حذف" />
                </div>
                {services.map((row) => {
                  const availTeam = staffFor(row.service?.id);
                  const off = row.isReserved && !row.isSelected;
                  const needsStaff = !off && !row.profession?.id;
                  return (
                    <div key={row.id} className={classes.itemRow} data-off={off} role="row">
                      <span role="cell">
                        {row.isReserved ? (
                          <Checkbox
                            color="violet"
                            aria-label={`افزودن ${row.service?.title ?? "نوبت"} به فاکتور`}
                            checked={row.isSelected}
                            onChange={(e) => handleReservedSelection(row, e.currentTarget.checked)}
                          />
                        ) : (
                          <span className={classes.walkInDot} title="خدمت حضوری" />
                        )}
                      </span>
                      <span role="cell" className={classes.itemTitle}>
                        <strong>{row.service?.title}</strong>
                        {row.isReserved ? (
                          <span className={classes.itemMeta}>
                            <span className={classes.reservedTag}>رزرو</span>
                            {row.appointmentDate && `${moment(row.appointmentDate).format("jMM/jDD")} · ${row.startTime}–${row.endTime}`}
                          </span>
                        ) : (
                          <span className={classes.itemMeta}>حضوری</span>
                        )}
                        {row.hasPayment && (row.paymentPrice || 0) > 0 && (
                          <span className={classes.itemMeta}>بیعانه {rial(row.paymentPrice || 0)} · {rowGateway(row)}</span>
                        )}
                      </span>
                      <span role="cell">
                        <Select
                          aria-label="انجام‌دهنده"
                          value={row.profession?.id?.toString()}
                          data={availTeam.map((t) => ({ label: `${t.firstName} ${t.lastName}`, value: t.id?.toString() || "" }))}
                          onChange={(v) => handleUpdateTeam(row.id, v)}
                          placeholder="انتخاب انجام‌دهنده"
                          allowDeselect={false}
                          error={needsStaff}
                          searchable={availTeam.length > 6}
                        />
                      </span>
                      <span role="cell">
                        <NumberInput
                          aria-label="مبلغ"
                          value={row.price}
                          min={0}
                          hideControls
                          thousandSeparator=","
                          styles={{ input: { direction: "ltr", textAlign: "left" } }}
                          onChange={(v) => handleUpdatePrice(row.id, Number(v) || 0)}
                        />
                      </span>
                      <span role="cell">
                        <NumberInput
                          aria-label="درصد کمیسیون"
                          disabled={commissionLocked || isChairRenter(row.profession)}
                          title={isChairRenter(row.profession) ? "آرایشگر اجاره‌ای؛ کمیسیون ندارد" : undefined}
                          value={isChairRenter(row.profession) ? "" : row.percentage}
                          min={0}
                          max={100}
                          hideControls
                          suffix="٪"
                          styles={{ input: { direction: "ltr", textAlign: "left" } }}
                          onChange={(v) => handleUpdatePercentage(row.id, Number(v) || 0)}
                        />
                      </span>
                      <span role="cell">
                        {row.isRemovable && (
                          <ActionIcon size="lg" color="red" variant="subtle" aria-label={`حذف ${row.service?.title ?? "خدمت"}`} onClick={() => handleRemoveService(row.id)}>
                            <IconTrash size={17} />
                          </ActionIcon>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {(paidServices?.length || 0) > 0 && (
              <div className={classes.paid}>
                <span className={classes.paidTitle}>قبلاً فاکتور شده</span>
                {(paidServices as AppointmentQueueItemDto[])?.map((svc) => (
                  <div key={svc.appointmentId} className={classes.paidRow}>
                    <span>
                      <strong>{svc?.merchantServiceTitle}</strong>
                      <span>
                        {svc?.merchantUserFirstName} {svc.merchantUserLastName} · {moment(svc.appointmentDate).format("jYYYY/jMM/jDD")} ·{" "}
                        {moment(svc.startTime, "HH:mm:ss").format("HH:mm")}–{moment(svc.endTime, "HH:mm:ss").format("HH:mm")}
                      </span>
                    </span>
                    <span className={classes.paidTag}>پرداخت شده</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className={`${classes.panel} ${classes.summary}`} aria-label="خلاصه فاکتور">
          <div className={classes.summaryScroll}>
            <UnstyledButton className={classes.customer} onClick={openCustomer} data-empty={!selectedCustomer}>
              {selectedCustomer ? (
                <>
                  <Avatar color="violet" radius="xl">{selectedCustomer.firstName?.charAt(0) || ""}</Avatar>
                  <span className={classes.customerText}>
                    <strong>{`${selectedCustomer.firstName ?? ""} ${selectedCustomer.lastName ?? ""}`.trim() || "مشتری"}</strong>
                    <span dir="ltr">{selectedCustomer.phoneNumber}</span>
                  </span>
                  <span className={classes.change}>تغییر</span>
                </>
              ) : (
                <>
                  <span className={classes.customerIcon}><IconUserPlus size={20} /></span>
                  <span className={classes.customerText}>
                    <strong>انتخاب مشتری</strong>
                    <span>برای ثبت فاکتور و استفاده از کد تخفیف لازم است</span>
                  </span>
                </>
              )}
            </UnstyledButton>

            <dl className={classes.totals}>
              <div><dt>جمع خدمات</dt><dd>{rial(summary.totalService)}</dd></div>
              {summary.totalUserPayments > 0 && <div><dt>بیعانه پرداخت‌شده</dt><dd>−{rial(summary.totalUserPayments)}</dd></div>}
              {discount.amount > 0 && <div className={classes.discountLine}><dt>تخفیف{discount.code && <> (<span className={classes.code} dir="ltr">{discount.code}</span>)</>}</dt><dd>−{rial(discount.amount)}</dd></div>}
              <div className={classes.totalLine}><dt>قابل پرداخت</dt><dd>{rial(payable)}</dd></div>
              <div className={classes.shareLine}><dt>سهم مجموعه (کمیسیون)</dt><dd>{rial(summary.totalCommission)}</dd></div>
            </dl>

            <div className={classes.block}>
              <InvoiceDiscount collapsible customerId={selectedCustomer?.id} owed={owed} onChange={setDiscount} />
            </div>

            <div className={classes.block}>
              <span className={classes.blockTitle} id="invoice-method">روش دریافت</span>
              <div className={classes.methods} role="radiogroup" aria-labelledby="invoice-method">
                {directMethods.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="radio"
                    aria-checked={method === item.value}
                    className={classes.method}
                    onClick={() => {
                      setMethod(item.value);
                      setWalkInPaymentMethod(directMethodValue[item.value]);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {review.row && <div className={classes.block}>{review.row}</div>}

            <div className={classes.block}>
              {noteOpen || note ? (
                <Textarea label="یادداشت" placeholder="یادداشت این فاکتور (اختیاری)" autosize minRows={2} maxRows={4} value={note} onChange={(e) => setNote(e.currentTarget.value)} autoFocus={noteOpen && !note} />
              ) : (
                <button type="button" className={classes.linkButton} onClick={() => setNoteOpen(true)}>
                  <IconNote size={17} aria-hidden="true" />افزودن یادداشت
                </button>
              )}
            </div>
          </div>

          <div className={classes.summaryFooter}>
            <Button
              fullWidth
              size="lg"
              color="violet"
              onClick={createFactor}
              loading={isLoading}
              disabled={review.blocked || discount.pending || activeServices.length === 0 || !selectedCustomer || summary.totalService <= 0}
            >
              <span className={classes.ctaText}>ثبت فاکتور<span>{rial(payable)}</span></span>
            </Button>
            {activeServices.length > 0 && !selectedCustomer && <p className={classes.ctaHint}>برای ثبت فاکتور اول مشتری را انتخاب کنید.</p>}
          </div>
        </aside>
      </div>

      {!catalogInline && (
        <Drawer opened={catalogOpened} onClose={closeCatalog} position="right" size={380} withCloseButton>
          {catalog}
        </Drawer>
      )}

      <SelectCustomer previewCustomer={previewCustomer} closeCustomer={closeCustomer} setSelectedCustomer={setSelectedCustomer} />
    </div>
  );
}

export default AddPaymentDesktopView;
